import { useEffect, useRef, useState, useMemo } from 'react';
import { 
  //Upload, 
  Search, X, ChevronRight, AlertTriangle, Trash2, Download } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { SparkActivityListItem, SparkActivityFilters, SparkActivityStatus } from '../../types/spark-activity';
import { useSparkActivityFilterOptions } from '../../hooks/useSparkActivities';
import SkillsGroupedSelect from './SkillsGroupedSelect';
import PineSelect from './PineSelect';

const STATUS_OPTIONS: Array<{ value: SparkActivityStatus | null; label: string }> = [
  { value: null, label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Live' },
  { value: 'unpublished', label: 'Unpublished' },
  { value: 'archived', label: 'Archived' },
];

const STATUS_COLORS: Record<SparkActivityStatus, string> = {
  draft: 'bg-pine-draft',
  published: 'bg-pine-approved',
  unpublished: 'bg-pine-line',
  archived: 'bg-pine-danger',
};

interface ActivitySidebarProps {
  filters: SparkActivityFilters;
  onUpdateFilter: <K extends keyof SparkActivityFilters>(key: K, value: SparkActivityFilters[K]) => void;
  onClearFilters: () => void;
  activities: SparkActivityListItem[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onDelete: (id: number) => void;
  onDeleteBatch: (batchName: string, batchId: number, count: number) => void;
  isLoading: boolean;
  onUploadClick: () => void;
  onDownloadClick: () => void;
  onNewClick: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export default function ActivitySidebar({
  filters,
  onUpdateFilter,
  onClearFilters,
  activities,
  selectedId,
  onSelect,
  onDelete,
  onDeleteBatch,
  isLoading,
  //onUploadClick,
  onDownloadClick,
  onNewClick,
  collapsed = false,
  onToggleCollapse,
}: ActivitySidebarProps) {
  const { data: filterOptions } = useSparkActivityFilterOptions();

  const [searchDraft, setSearchDraft] = useState(filters.search);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (searchDraft !== filters.search) {
        onUpdateFilter('search', searchDraft);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchDraft, filters.search, onUpdateFilter]);

  const groupedByBatch = useMemo(
    () =>
      activities.reduce<Record<string, SparkActivityListItem[]>>(
        (acc, item) => {
          const key = item.batchName ?? 'Unbatched';
          if (!acc[key]) acc[key] = [];
          acc[key].push(item);
          return acc;
        },
        {}
      ),
    [activities]
  );

  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(() => new Set());

  // Update expanded batches when activities change
  useEffect(() => {
    const batchNames = Object.keys(groupedByBatch);
    setExpandedBatches((prev) => {
      const next = new Set(prev);
      // Add any new batches (expanded by default)
      batchNames.forEach((name) => {
        if (!next.has(name)) {
          next.add(name);
        }
      });
      // Remove batches that no longer exist
      Array.from(next).forEach((name) => {
        if (!batchNames.includes(name)) {
          next.delete(name);
        }
      });
      return next;
    });
  }, [activities]);

  const toggleBatch = (batchName: string) => {
    setExpandedBatches((prev) => {
      const next = new Set(prev);
      if (next.has(batchName)) {
        next.delete(batchName);
      } else {
        next.add(batchName);
      }
      return next;
    });
  };

  if (collapsed) {
    return (
      <aside
        className="border border-pine-line rounded-[14px] shadow-[0_1px_2px_rgba(23,63,56,0.06),0_6px_20px_rgba(23,63,56,0.07)] flex flex-col items-center overflow-hidden bg-pine-card font-body transition-all duration-200 ease-out"
        style={{ width: '60px', flexShrink: 0 }}
      >
        <div className="p-3 border-b border-pine-line w-full flex justify-center">
          <button
            onClick={onToggleCollapse}
            className="p-1.5 rounded-md hover:bg-pine-hover-item transition-colors text-pine-ink-soft hover:text-pine-ink"
            title="Expand sidebar"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 flex items-start justify-center pt-3">
          <span className="text-pine-ink-soft text-[10px] font-semibold" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', letterSpacing: '0.08em' }}>
            {activities.length} activities
          </span>
        </div>
      </aside>
    );
  }

  return (
    <aside
      data-testid="activity-sidebar"
      className="border border-pine-line rounded-[14px] shadow-[0_1px_2px_rgba(23,63,56,0.06),0_6px_20px_rgba(23,63,56,0.07)] flex flex-col overflow-hidden bg-pine-card font-body transition-all duration-200 ease-out"
      style={{ width: '330px', flexShrink: 0 }}
    >
      <div className="p-3 border-b border-pine-line flex items-center justify-between">
        <span className="text-sm font-semibold text-pine-ink">Activities</span>
        <div className="flex items-center gap-1">
          <button
            onClick={onNewClick}
            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-pine-pine hover:bg-pine-pine-deep text-white font-semibold rounded-md transition-colors"
          >
            + New
          </button>
          {/* <button
            onClick={onUploadClick}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-pine-gold hover:bg-pine-gold-deep text-[#2A1F08] font-semibold rounded-md shadow-[0_2px_8px_rgba(232,161,60,.4)] transition-colors"
          >
            <Upload className="w-3.5 h-3.5" />
            Upload CSV
          </button> */}
          
          <button
            onClick={onDownloadClick}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-pine-pine hover:bg-pine-pine-deep text-white font-semibold rounded-md transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Export
          </button>
           
          <button
            onClick={onToggleCollapse}
            className="p-1.5 rounded-md hover:bg-pine-hover-item transition-colors text-pine-ink-soft hover:text-pine-ink"
            title="Collapse sidebar"
          >
            <ChevronRight className="w-3.5 h-3.5 rotate-180" />
          </button>
        </div>
      </div>

      <div className="p-3 space-y-2 border-b border-pine-line">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-pine-ink-soft" />
          <input
            type="text"
            placeholder="Search activities…"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-pine-line rounded-md text-pine-ink placeholder-pine-ink-soft focus:outline-none hover:border-pine-pine focus:border-pine-pine"
          />
          {searchDraft && (
            <button
              onClick={() => { setSearchDraft(''); onUpdateFilter('search', ''); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-pine-ink-soft hover:text-pine-ink"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <PineSelect
            options={[
              { value: '', label: 'All domains' },
              ...(filterOptions?.domains ?? []).map((d) => ({ value: d.id, label: d.title })),
            ]}
            value={filters.domainId ?? ''}
            onChange={(v) => onUpdateFilter('domainId', v === '' ? null : Number(v))}
            placeholder="All domains"
          />

          <SkillsGroupedSelect
            skills={filterOptions?.skills.filter((s) => !filters.domainId || s.domainId === filters.domainId) ?? []}
            value={filters.skillId ?? null}
            onChange={(id) => onUpdateFilter('skillId', id)}
          />
        </div>

        <div className="flex gap-1 flex-wrap">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={String(opt.value)}
              onClick={() => onUpdateFilter('status', opt.value)}
              className={`px-2 py-0.5 text-xs rounded-pine-pill border transition-colors ${
                filters.status === opt.value
                  ? 'bg-pine-pine border-pine-pine text-white'
                  : 'border-pine-line text-pine-ink-soft hover:text-pine-ink hover:border-pine-pine'
              }`}
            >
              {opt.label}
            </button>
          ))}
          {(filters.search || filters.domainId || filters.skillId || filters.status) && (
            <button
              onClick={onClearFilters}
              className="px-2 py-0.5 text-xs rounded-pine-pill border border-pine-line text-pine-ink-soft hover:text-pine-ink transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-1.5">
        {isLoading ? (
          <div className="p-4 text-center text-xs text-pine-ink-soft">Loading…</div>
        ) : activities.length === 0 ? (
          <div className="p-4 text-center text-xs text-pine-ink-soft">No activities found</div>
        ) : (
          Object.entries(groupedByBatch).map(([batchName, items]) => {
            const isExpanded = expandedBatches.has(batchName);
            return (
              <div key={batchName}>
                <div className="w-full px-[10px] pt-3 pb-1 flex items-center gap-2 bg-white sticky top-0 hover:bg-pine-hover-item transition-colors group/batch">
                  <button
                    onClick={() => toggleBatch(batchName)}
                    className="flex items-center gap-2 flex-1 min-w-0"
                  >
                    <motion.span
                      animate={{ rotate: isExpanded ? 90 : 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      className="flex-shrink-0"
                    >
                      <ChevronRight className="w-3 h-3 text-pine-ink-soft group-hover/batch:text-pine-ink" />
                    </motion.span>
                    <span className="text-pine-ink-soft uppercase tracking-[0.12em] text-pine-label font-bold truncate">
                      {batchName}
                    </span>
                    <span className="text-pine-ink-soft text-pine-label font-normal">
                      ({items.length})
                    </span>
                  </button>
                  {batchName !== 'Unbatched' && items[0]?.batchId && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onDeleteBatch(batchName, items[0].batchId!, items.length); }}
                      className="mr-6 opacity-0 group-hover/batch:opacity-100 p-0.5 rounded hover:bg-pine-danger/10 text-pine-ink-soft hover:text-pine-danger transition-all flex-shrink-0"
                      title="Delete batch"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      className="overflow-hidden flex flex-col gap-[3px] p-1"
                    >
                      {items.map((item, index) => (
                        <button
                          key={item.id}
                          data-key={item.key}
                          onClick={() => onSelect(item.id)}
                          className={`group relative w-full text-left px-[10px] py-[9px] flex items-center gap-2.5 rounded-[10px] hover:bg-pine-hover-item transition-colors ${
                            selectedId === item.id ? 'bg-pine-active-item ring-[1.5px] ring-inset ring-pine-pine' : ''
                          }`}
                        >
                          <span className="w-5 text-right text-xs text-pine-ink-soft flex-shrink-0 tabular-nums">{index + 1}</span>
                          <span className="flex-1 text-xs text-pine-ink truncate">{item.title}</span>
                          {item.hasMismatch && (
                            <span className="text-amber-500 flex-shrink-0" title="Has unresolved mismatches">
                              <AlertTriangle className="w-3.5 h-3.5" />
                            </span>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
                            className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-pine-danger/10 text-pine-ink-soft hover:text-pine-danger transition-all flex-shrink-0"
                            title="Delete activity"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                          <span
                            className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_COLORS[item.status]}`}
                          />
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
}
